import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Button, CircularProgress, Divider, MenuItem, Stack, TextField, Typography,
} from '@mui/material';
import { editorTheme, sectionHeadingSx } from '../utils/editor-theme';
import DrawingImport from '../../dashboard/components/drawing-import.component';
import { EDITOR_FEATURES } from '../utils/editor-features';
import type { DrawingConversion } from '../../dashboard/services/drawing-import.service';
import { normalizeFloorPlan, type StoredFloorPlan } from '../utils/normalize-floorplan';
import type { EditableFloorPlan } from '../models/editor-feature';

/** The five layers a GeoJSON file can be imported into, named as the database names them. */
type LayerKey = 'borderLayer' | 'floorLayer' | 'roomLayer' | 'workplacesLayer' | 'labelLayer';

/** Which editor collection each layer's envelope belongs to. */
const ENVELOPE_OF: Readonly<Record<LayerKey, 'border' | 'floor' | 'room' | 'workplaces' | 'label'>> = {
  borderLayer: 'border',
  floorLayer: 'floor',
  roomLayer: 'room',
  workplacesLayer: 'workplaces',
  labelLayer: 'label',
};

const envelopesFor = (
  layer: LayerKey,
  normalised: EditableFloorPlan,
): Partial<EditableFloorPlan['layerEnvelopes']> => ({
  [ENVELOPE_OF[layer]]: normalised.layerEnvelopes[ENVELOPE_OF[layer]],
});

const LAYERS: ReadonlyArray<{ readonly key: LayerKey; readonly labelKey: string }> = [
  { key: 'borderLayer', labelKey: 'floorplan-editor-layer-border' },
  { key: 'floorLayer', labelKey: 'floorplan-editor-layer-floor' },
  { key: 'roomLayer', labelKey: 'floorplan-editor-layer-rooms' },
  { key: 'workplacesLayer', labelKey: 'floorplan-editor-layer-desks' },
  { key: 'labelLayer', labelKey: 'floorplan-editor-layer-labels' },
];

interface Props {
  readonly officeId: string;
  readonly floorPlan: EditableFloorPlan;
  /** Applies the imported drawing as an ordinary, undoable edit. */
  readonly onImported: (next: EditableFloorPlan) => void;
}

/**
 * Bringing a drawing into the floorplan that is open.
 *
 * Here rather than only on the office page, because importing is not a one-off act of
 * creation — a floor gets re-drawn, a wing gets added, a consultant sends a revised DXF — and
 * every one of those means importing *into* a plan that already exists and already has desks
 * people have booked.
 *
 * Nothing here writes to the database. What is imported becomes an edit in the editor, which
 * means it can be undone, compared against what was there, and saved when the admin is
 * satisfied. An import that wrote straight through would be the one action in this tool with
 * no way back.
 */
const ImportPanel = ({ officeId, floorPlan, onImported }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const [layer, setLayer] = useState<LayerKey>('floorLayer');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  const [imported, setImported] = useState<string | undefined>();

  /** The plan as it stands, in the shape the normaliser reads. */
  const storedShape = (): StoredFloorPlan => ({
    id: floorPlan.floorPlanId,
    officeId: floorPlan.officeId,
    storey: floorPlan.storey,
    unitsPerMetre: floorPlan.unitsPerMetre,
  });

  const applyConversion = (conversion: DrawingConversion): void => {
    onImported(normalizeFloorPlan({
      ...storedShape(),
      borderLayer: conversion.borderLayer,
      floorLayer: conversion.floorLayer,
      roomLayer: conversion.roomLayer,
      workplacesLayer: conversion.workplacesLayer,
      labelLayer: conversion.labelLayer,
      unitsPerMetre: conversion.unitsPerMetre,
    }));

    setImported(String(t('floorplan-editor-import-applied')));
  };

  /**
   * One GeoJSON file into one layer.
   *
   * Deliberately per layer. A floorplan is five files in this platform's own export, and the
   * commonest import is not all five — it is "the rooms changed", which is one.
   *
   * Only the imported layer is normalised, and only the collections it produces are spliced
   * in. Round-tripping the whole plan through the serialiser instead would have been shorter
   * and silently destructive: `serializeFloorPlan` does not write line geometry — that is the
   * geometry the editor cannot model, which the server re-attaches on save — so importing a
   * rooms file would have wiped the floor drawing of every plan that keeps its floor as
   * lines. The data would survive the next save; what the admin saw would not.
   */
  const importGeoJson = async (file: File): Promise<void> => {
    setBusy(true);
    setError(undefined);
    setImported(undefined);

    let parsed: unknown;

    try {
      parsed = JSON.parse(await file.text());
    } catch {
      // Told apart from every other failure below. A file that is not JSON is the commonest
      // mistake here — a .dxf picked in the GeoJSON box — and it is the only one where
      // "this is not a GeoJSON file" is the truth rather than a guess.
      setError(String(t('floorplan-editor-import-not-json', { file: file.name })));
      setBusy(false);
      return;
    }

    try {
      const normalised = normalizeFloorPlan({ ...storedShape(), [layer]: parsed });

      onImported({
        ...floorPlan,
        ...(layer === 'borderLayer' ? { border: normalised.border } : {}),
        ...(layer === 'roomLayer' ? { rooms: normalised.rooms } : {}),
        ...(layer === 'floorLayer'
          ? {
            floorBackdrop: normalised.floorBackdrop,
            floorLines: normalised.floorLines,
            deskShapes: normalised.deskShapes,
          }
          : {}),
        ...(layer === 'workplacesLayer' ? { desks: normalised.desks } : {}),
        ...(layer === 'labelLayer' ? { labels: normalised.labels } : {}),

        // The envelope of the imported layer travels with it — `name` and anything else the
        // collection carried — while the other four keep the ones they arrived with.
        layerEnvelopes: { ...floorPlan.layerEnvelopes, ...envelopesFor(layer, normalised) },
      });

      setImported(String(t('floorplan-editor-import-applied')));
    } catch {
      setError(String(t('floorplan-editor-import-failed')));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Stack spacing={2} sx={{ p: 2, minHeight: 0, overflowY: 'auto' }}>
      <Typography sx={sectionHeadingSx}>{t('floorplan-editor-import-geojson')}</Typography>
      <Typography variant="caption" color={editorTheme.inkDim}>
        {t('floorplan-editor-import-geojson-hint')}
      </Typography>

      <TextField
        select
        size="small"
        value={layer}
        label={String(t('floorplan-editor-import-into'))}
        onChange={(event): void => setLayer(event.target.value as LayerKey)}
      >
        {LAYERS.map(({ key, labelKey }) => (
          <MenuItem key={key} value={key}>{t(labelKey)}</MenuItem>
        ))}
      </TextField>

      <Button size="small" variant="outlined" component="label" disabled={busy}>
        {t('floorplan-editor-import-choose-file')}
        <input
          type="file"
          hidden
          // Gated while a read is in flight: two files read at once would both splice into the
          // document, and whichever finished last would win by chance.
          disabled={busy}
          accept=".geojson,.json,application/geo+json,application/json"
          onChange={(event): void => {
            const file = event.target.files?.[0];

            // Cleared so choosing the same file twice fires a change event the second time.
            // eslint-disable-next-line no-param-reassign
            event.target.value = '';

            if (file && !busy) {
              importGeoJson(file).catch(() => setError(String(t('floorplan-editor-import-failed'))));
            }
          }}
        />
      </Button>

      {busy && <CircularProgress size={16} />}
      {error && <Alert severity="error" sx={{ py: 0 }}>{error}</Alert>}
      {imported && <Alert severity="success" sx={{ py: 0 }}>{imported}</Alert>}

      {EDITOR_FEATURES.drawingImport && (
        <>
          <Divider />

          <DrawingImport officeId={officeId} onConverted={applyConversion} />

          <Divider />

          <Typography sx={sectionHeadingSx}>{t('floorplan-editor-import-other-formats')}</Typography>
          <Typography variant="caption" color={editorTheme.inkDim}>
            {t('floorplan-editor-import-other-formats-hint')}
          </Typography>
        </>
      )}
    </Stack>
  );
};

export default ImportPanel;
